// Cloud Functions Service for AAR Guest Management System
import { httpsCallable } from "./guardedWrites";
import { functions } from "./config";

/**
 * Create a new user
 * @param {Object} userData - User data (email, firstName, lastName, role, password)
 * @returns {Promise<Object>} Creation result
 */
export const createUser = async (userData) => {
  try {
    const createFunction = httpsCallable(functions, "manageUser");
    const result = await createFunction({
      action: "create",
      ...userData
    });
    return result.data;
  } catch (error) {
    console.error("Error creating user:", error);
    throw error;
  }
};

/**
 * Delete user
 * @param {string} userId
 * @returns {Promise<Object>} Delete result
 */
export const deleteUser = async (userId) => {
  try {
    const deleteFunction = httpsCallable(functions, "manageUser");
    const result = await deleteFunction({
      action: "delete",
      userId: userId
    });
    return result.data;
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};

/**
 * Get data completeness analysis for participants
 * @param {string} groupId - Optional group ID (defaults to '1269')
 * @returns {Promise<Object>} Analysis results with statistics
 */
export const getDataCompletenessAnalysis = async (groupId = '1269') => {
  try {
    const analyzeFunction = httpsCallable(functions, "analyzeDataCompleteness");
    const result = await analyzeFunction({ groupId });
    return result.data;
  } catch (error) {
    console.error("Error analyzing data completeness:", error);
    throw error;
  }
};

/**
 * Update a profile in CCB
 * @param {Object} data - Update data
 * @param {string} data.individualId - The CCB individual ID
 * @param {Object} data.profileData - The profile data to update
 * @returns {Promise<Object>} Update result
 */
export const updateCCBProfile = async (data) => {
  try {
    const updateFunction = httpsCallable(functions, "updateCCBProfile");
    const result = await updateFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error updating CCB profile:", error);
    throw error;
  }
};

/**
 * Create a new profile in CCB
 * @param {Object} data - Profile data
 * @param {Object} data.profileData - The profile data for the new individual
 * @returns {Promise<Object>} Creation result with CCB ID
 */
export const createCCBProfile = async (data) => {
  try {
    const createFunction = httpsCallable(functions, "createCCBProfile");
    const result = await createFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error creating CCB profile:", error);
    throw error;
  }
};

/**
 * Add an individual to CCB group 1269
 * Called automatically during check-in for CCB-linked guests
 * @param {Object} data
 * @param {string} data.individualId - CCB individual ID (with or without ccb_ prefix)
 * @returns {Promise<Object>} Result
 */
export const addToCCBGroup = async (data) => {
  try {
    const addFunction = httpsCallable(functions, "addToCCBGroup");
    const result = await addFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error adding to CCB group:", error);
    // Don't throw - this is a non-critical background operation
    return { success: false, error: error.message };
  }
};

/**
 * Sync profiles from CCB to Firestore
 * @param {Object} data - Sync options
 * @param {string} data.groupId - Optional: specific group ID to sync (defaults to '1269')
 * @param {Array} data.individualIds - Optional: specific individual IDs to sync
 * @returns {Promise<Object>} Sync results
 */
export const syncFromCCB = async (data = {}) => {
  try {
    const syncFunction = httpsCallable(functions, "syncFromCCB");
    const result = await syncFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error syncing from CCB:", error);
    throw error;
  }
};

// ==================== CCB Event Attendance ====================

/**
 * Record event attendance in CCB
 * Called during guest and volunteer check-in to log attendance to CCB events
 * Non-critical background operation - errors are logged but not thrown
 * @param {Object} data
 * @param {string} data.serviceType - Service type ('9am', '12pm', '3pm', 'tuesday_prayer')
 * @param {string} data.checkInType - 'guest' or 'volunteer'
 * @param {Array<string>} data.individualIds - CCB individual IDs (with or without ccb_ prefix)
 * @param {string} data.notes - Optional notes
 * @returns {Promise<Object>} Result
 */
export const recordCCBEventAttendance = async (data) => {
  try {
    const attendanceFunction = httpsCallable(functions, "recordCCBEventAttendance");
    const result = await attendanceFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error recording CCB event attendance:", error);
    // Don't throw - this is a non-critical background operation
    return { success: false, error: error.message };
  }
};

/**
 * Update CCB event head count (total attendance including additional)
 * Called when additional attendance is saved on the dashboard
 * @param {Object} data
 * @param {string} data.serviceType - Service type ('9am', '12pm', '3pm', 'tuesday_prayer')
 * @param {string} data.checkInType - 'guest' or 'volunteer'
 * @param {number} data.headCount - Total head count
 * @param {string} data.dateStr - Date string YYYY-MM-DD
 * @returns {Promise<Object>} Result
 */
export const updateCCBEventHeadCount = async (data) => {
  try {
    const updateFunction = httpsCallable(functions, "updateCCBEventHeadCount");
    const result = await updateFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error updating CCB event head count:", error);
    return { success: false, error: error.message };
  }
};

// ==================== SMS Functions ====================

/**
 * Send transportation arrival SMS to a checked-in guest
 * @param {Object} data
 * @param {string} data.checkInId - The check-in record ID
 * @param {string} data.guestId - Guest participant ID
 * @param {string} data.guestFirstName - Guest first name (for message personalization)
 * @param {string} data.guestLastName - Guest last name
 * @param {string} data.phoneNumber - Guest phone number
 * @returns {Promise<Object>} Result with smsId
 */
export const sendTransportationSMS = async (data) => {
  try {
    const sendFunction = httpsCallable(functions, "sendTransportationSMS");
    const result = await sendFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error sending transportation SMS:", error);
    throw error;
  }
};

// ==================== CCB V2 API Functions ====================

/**
 * Create a new profile in CCB using V2 API with image upload support
 * This is the preferred method for creating new guests with photos
 * @param {Object} data - Profile data and optional image
 * @param {Object} data.profileData - The profile data for the new individual
 * @param {string} data.imageBase64 - Optional: Base64 encoded image data
 * @param {string} data.imageContentType - Optional: Image MIME type (default: 'image/jpeg')
 * @returns {Promise<Object>} Creation result with CCB ID and image upload status
 */
export const createCCBProfileV2 = async (data) => {
  try {
    const createFunction = httpsCallable(functions, "createCCBProfileV2");
    const result = await createFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error creating CCB profile (V2):", error);
    throw error;
  }
};

/**
 * Upload image to existing CCB profile using V2 API
 * @param {Object} data - Image upload data
 * @param {string} data.individualId - The CCB individual ID (numeric, without ccb_ prefix)
 * @param {string} data.imageBase64 - Base64 encoded image data
 * @param {string} data.imageContentType - Optional: Image MIME type (default: 'image/jpeg')
 * @returns {Promise<Object>} Upload result
 */
export const uploadCCBProfileImageV2 = async (data) => {
  try {
    const uploadFunction = httpsCallable(functions, "uploadCCBProfileImageV2", { timeout: 120000 });
    const result = await uploadFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error uploading CCB profile image (V2):", error);
    throw error;
  }
};

/**
 * Delete the profile image of a CCB individual via V2 API.
 * @param {Object} data
 * @param {string} data.individualId - CCB numeric ID (without ccb_ prefix)
 */
export const deleteCCBProfileImageV2 = async (data) => {
  try {
    const fn = httpsCallable(functions, "deleteCCBProfileImageV2", { timeout: 60000 });
    const result = await fn(data);
    return result.data;
  } catch (error) {
    console.error("Error deleting CCB profile image (V2):", error);
    throw error;
  }
};

/**
 * Get CCB V2 authorization status
 * Returns whether OAuth is configured and provides auth URL if needed
 * @returns {Promise<Object>} Authorization status and URL
 */
export const getCCBV2AuthStatus = async () => {
  try {
    const statusFunction = httpsCallable(functions, "getCCBV2AuthStatus");
    const result = await statusFunction();
    return result.data;
  } catch (error) {
    console.error("Error getting CCB V2 auth status:", error);
    throw error;
  }
};

/**
 * Complete CCB V2 OAuth authorization with authorization code
 * @param {Object} data - Authorization data
 * @param {string} data.code - The authorization code from OAuth redirect
 * @param {string} data.redirectUri - The redirect URI used in the auth request
 * @returns {Promise<Object>} Authorization result
 */
export const authorizeCCBV2 = async (data) => {
  try {
    const authFunction = httpsCallable(functions, "authorizeCCBV2");
    const result = await authFunction(data);
    return result.data;
  } catch (error) {
    console.error("Error authorizing CCB V2:", error);
    throw error;
  }
};
